import { nanoid } from 'nanoid';
import { DIMENSION_PRESETS, MATTRESS_SCHEMA_VERSION } from './types';
import { computeTotalHeightMm, reindexLayers } from './rules';
import { materialRegistry } from '../materials/registry';
import { recommendComposition, } from './recommendation';
export function createLayer(input) {
    const espessura = input.espessuraMm ?? input.material.alturaDefaultMm;
    return {
        id: nanoid(10),
        nome: input.nome ?? input.material.nome,
        role: input.role,
        materialId: input.material.id,
        espessuraMm: espessura,
        densidadeOpcional: input.material.densidade,
        firmezaOpcional: input.material.firmeza,
        ordem: input.ordem,
        visible: true,
        locked: false,
    };
}
export function createProject(input) {
    const preset = input.preset ? DIMENSION_PRESETS[input.preset] : DIMENSION_PRESETS.casal;
    const larguraMm = input.larguraMm ?? preset.larguraMm;
    const comprimentoMm = input.comprimentoMm ?? preset.comprimentoMm;
    const camadas = reindexLayers(input.camadasIniciais ?? []);
    const alturaTotalMm = computeTotalHeightMm(camadas);
    const now = new Date().toISOString();
    const acabamento = {
        topType: input.acabamento?.topType ?? 'quilted',
        lateralAcolchoada: input.acabamento?.lateralAcolchoada ?? true,
        viesAplicado: input.acabamento?.viesAplicado ?? true,
        quiltPattern: input.acabamento?.quiltPattern ?? 'diamond',
    };
    return {
        schemaVersion: MATTRESS_SCHEMA_VERSION,
        id: nanoid(12),
        nome: input.nome ?? 'Colchão sem nome',
        dataCriacao: now,
        dataAtualizacao: now,
        dimensoes: { larguraMm, comprimentoMm, alturaTotalMm },
        listaDeCamadas: camadas,
        acabamento,
        tecidoSuperiorId: input.tecidoSuperiorId,
        tecidoLateralId: input.tecidoLateralId,
        ...(input.perfilCliente ? { perfilCliente: input.perfilCliente } : {}),
    };
}
/**
 * Cria um projeto novo a partir de um perfil do cliente — usa o motor de
 * recomendação para montar composição, acabamento e tecidos.
 */
export function createProjectFromRecommendation(input) {
    const recommendation = recommendComposition(input.perfilCliente);
    const camadas = recommendation.camadas
        .map((layerRec, i) => {
        const mat = materialRegistry.getMaterial(layerRec.materialId);
        if (!mat)
            return null;
        return createLayer({
            material: mat,
            role: layerRec.role,
            espessuraMm: layerRec.espessuraMm,
            ordem: i,
        });
    })
        .filter((l) => l !== null);
    const project = createProject({
        nome: input.nome,
        preset: input.preset,
        larguraMm: input.larguraMm,
        comprimentoMm: input.comprimentoMm,
        tecidoSuperiorId: recommendation.tecidoSuperiorId,
        tecidoLateralId: recommendation.tecidoLateralId,
        camadasIniciais: reindexLayers(camadas),
        acabamento: { topType: recommendation.tampo },
        perfilCliente: input.perfilCliente,
    });
    return { project: touchProject(project), recommendation };
}
/**
 * Gera um projeto de partida convincente: núcleo HR + transição + conforto + pillow quiltado.
 * Usado em "novo projeto" para que o usuário já veja algo relevante no 3D.
 */
export function createStarterProject(coreMaterial, transitionMaterial, comfortMaterial, tecidoSuperiorId, tecidoLateralId, nome = 'Novo colchão') {
    const camadas = [
        createLayer({ material: coreMaterial, role: 'core', ordem: 0 }),
        createLayer({ material: transitionMaterial, role: 'transition', ordem: 1 }),
        createLayer({ material: comfortMaterial, role: 'top-comfort', ordem: 2 }),
    ];
    return createProject({
        nome,
        preset: 'casal',
        tecidoSuperiorId,
        tecidoLateralId,
        camadasIniciais: camadas,
    });
}
/** Atualiza `dataAtualizacao` e recalcula altura total */
export function touchProject(project) {
    return {
        ...project,
        dataAtualizacao: new Date().toISOString(),
        dimensoes: {
            ...project.dimensoes,
            alturaTotalMm: computeTotalHeightMm(project.listaDeCamadas),
        },
    };
}
